<template>
  <div class="letter-avatar-container" :title="fullName">
    <img
      v-if="shouldShowImage"
      :src="src"
      :alt="fullName"
      :style="avatarSize"
      class="avatar-image"
      @error="handleImageError"
    />
    <div v-else :style="[avatarSize, avatarStyle]" class="avatar-fallback">
      <span>{{ initialLetter }}</span>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

@Component
export default class LetterAvatar extends Vue {
  @Prop({ type: String, default: undefined })
  readonly src!: string | undefined;
  @Prop({ type: String, required: true, default: "User" })
  readonly fullName!: string;

  @Prop({ type: [Number, String], default: null })
  readonly userId!: number | string | null;

  @Prop({ type: Number, default: 40 })
  readonly size!: number;

  @Prop({ type: String, default: null })
  readonly ignoreSrcPattern!: string | null;

  private isImageError: boolean = false;

  private readonly colors: string[] = [
    "#40CBDE",
    "#0B759D",
    "#0DC181",
    "#FFBE56",
    "#FF9A66",
    "#F47459",
    "#5CACFF",
    "#898AF5",
    "#58D7A9",
    "#3B3C66",
  ];

  get initialLetter(): string {
    if (!this.fullName) return "?";
    const cleanedName = this.fullName.replace(/[^A-Za-zก-ฮ]/g, "");
    return cleanedName ? cleanedName.charAt(0).toUpperCase() : "?";
  }

  get backgroundColor(): string {
    if (this.userId != null) {
      const idStr = String(this.userId);
      const lastDigit = parseInt(idStr.slice(-1), 10);
      if (!isNaN(lastDigit) && lastDigit < this.colors.length) {
        return this.colors[lastDigit];
      }
    }

    if (!this.fullName) return "#bdc3c7";
    let hash = 0;
    for (let i = 0; i < this.fullName.length; i++) {
      hash = this.fullName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash % this.colors.length);
    return this.colors[index];
  }

  get avatarSize(): { [key: string]: string } {
    return {
      width: `${this.size}px`,
      height: `${this.size}px`,
    };
  }

  get avatarStyle(): { [key: string]: string } {
    return {
      backgroundColor: this.backgroundColor,
      fontSize: `${this.size * 0.5}px`,
    };
  }

  get shouldShowImage(): boolean {
    if (!this.src || this.isImageError) {
      return false;
    }
    if (this.ignoreSrcPattern && this.src.includes(this.ignoreSrcPattern)) {
      return false;
    }
    return true;
  }

  public handleImageError(): void {
    this.isImageError = true;
  }

  @Watch("src")
  onSrcChanged(): void {
    this.isImageError = false;
  }
}
</script>

<style scoped>
.letter-avatar-container,
.avatar-fallback,
.avatar-image {
  border-radius: 50%;
  object-fit: cover;
}

.avatar-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  text-transform: uppercase;
}
</style>
